/** @format */

import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { EChartsOption } from 'echarts'
import dayjs from 'dayjs'
import { _$ } from '@king-design/react'
import { EchartReact } from 'src/components'
import type { EchartReactHandle } from 'src/components/EchartReact'
import { echartTooltipOptions } from 'src/components/EchartReact/constants'

const LINE_COLOR = '#3083E4'
const MS_PER_DAY = 24 * 3600 * 1000
/** 与无数据态 grid.top 一致，用于计算空状态文案垂直居中 */
const CHART_TITLE_HEIGHT = 40

function getEmptyTextTop(colHeight: number) {
  if (colHeight <= CHART_TITLE_HEIGHT) {
    return colHeight / 2
  }
  return CHART_TITLE_HEIGHT + (colHeight - CHART_TITLE_HEIGHT) / 2
}

export interface OverviewLineChartPoint {
  Timestamp: string
  Value: number | null
}

export interface OverviewLineChartProps {
  className?: string
  title: string
  data?: OverviewLineChartPoint[]
  /** 错误率等百分比指标 */
  yPercent?: boolean
  /** 图表区域高度，按 colWidth 16:9 计算 */
  colHeight?: number
}

function parsePointTimestamp(timestamp: string) {
  const num = Number(timestamp)
  if (!Number.isNaN(num) && String(timestamp).trim() !== '') {
    return num < 1e12 ? dayjs(num * 1000) : dayjs(num)
  }
  return dayjs(timestamp)
}

/** 跨度不足 24h 且同一天 → HH:mm；跨天或跨度 ≥24h → MM-DD HH:mm */
function getAxisTimeFormat(points: OverviewLineChartPoint[]): string {
  if (points.length <= 1) return 'HH:mm'

  const sorted = [...points].sort(
    (a, b) =>
      parsePointTimestamp(a.Timestamp).valueOf() - parsePointTimestamp(b.Timestamp).valueOf()
  )
  const start = parsePointTimestamp(sorted[0].Timestamp)
  const end = parsePointTimestamp(sorted[sorted.length - 1].Timestamp)
  const rangeMs = Math.abs(end.valueOf() - start.valueOf())

  if (rangeMs < MS_PER_DAY && start.isSame(end, 'day')) {
    return 'HH:mm'
  }
  return 'MM-DD HH:mm'
}

function parseChartData(points?: OverviewLineChartPoint[]) {
  if (!points?.length) {
    return {
      xAxis: [] as string[],
      values: [] as (number | null)[],
      axisTimeFormat: 'HH:mm' as const
    }
  }
  const sorted = [...points].sort(
    (a, b) =>
      parsePointTimestamp(a.Timestamp).valueOf() - parsePointTimestamp(b.Timestamp).valueOf()
  )
  const axisTimeFormat = getAxisTimeFormat(sorted)
  return {
    xAxis: sorted.map(item => parsePointTimestamp(item.Timestamp).format(axisTimeFormat)),
    values: sorted.map(item => (item.Value != null ? Number(item.Value) : null)),
    axisTimeFormat
  }
}

const OverviewLineChart: React.FC<OverviewLineChartProps> = ({
  className,
  title,
  data,
  yPercent,
  colHeight
}) => {
  const chartRef = useRef<EchartReactHandle>(null)
  const hasData = Boolean(data?.length)

  const chartOption = useMemo((): EChartsOption => {
    const titleStyle = {
      fontSize: 14,
      fontWeight: 600,
      color: 'rgba(0,0,0,0.85)'
    }
    if (!data?.length) {
      return {
        title: {
          text: title,
          left: 12,
          top: 0,
          textStyle: titleStyle
        },
        grid: {
          left: 12,
          right: 44,
          top: 40,
          bottom: 12
        },
        xAxis: { show: false },
        yAxis: { show: false },
        series: [],
        tooltip: { show: false },
        graphic: {
          type: 'text',
          left: 'center',
          top: getEmptyTextTop(colHeight ?? 0) - 6,
          style: {
            text: _$('暂无数据'),
            fill: 'rgba(0,0,0,0.45)',
            fontSize: 12,
            align: 'center'
          }
        }
      }
    }

    const { xAxis: xAxisLabels, values: seriesData, axisTimeFormat } = parseChartData(data)
    const isLongXLabel = axisTimeFormat === 'MM-DD HH:mm'
    const yAxis: EChartsOption['yAxis'] = {
      type: 'value',
      min: 0,
      ...(yPercent ? { max: 100, interval: 20 } : {}),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: 'rgba(0,0,0,0.45)',
        fontSize: 11,
        ...(yPercent ? { formatter: '{value}%' } : {})
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: '#e8e8e8'
        }
      }
    }

    return {
      color: [LINE_COLOR],
      graphic: [],
      title: {
        text: title,
        left: 12,
        top: 0,
        textStyle: titleStyle
      },
      grid: {
        left: 12,
        right: 8,
        top: 40,
        bottom: isLongXLabel ? 8 : 12,
        containLabel: true
      },
      tooltip: {
        ...echartTooltipOptions,
        confine: true,
        axisPointer: {
          type: 'line',
          lineStyle: { color: LINE_COLOR, type: 'dashed', width: 1 }
        },
        textStyle: { fontSize: 12 },
        valueFormatter: (v: unknown) => {
          if (v == null || v === '') {
            return '-'
          }
          return yPercent && typeof v === 'number' ? `${v}%` : String(v)
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: xAxisLabels,
        axisLine: { lineStyle: { color: '#ebebeb' } },
        axisTick: { show: false },
        axisLabel: {
          color: 'rgba(0,0,0,0.45)',
          fontSize: 11,
          hideOverlap: true
        },
        splitLine: { show: false }
      },
      yAxis,
      series: [
        {
          type: 'line',
          name: title,
          smooth: true,
          symbol: 'circle',
          symbolSize: 5,
          // showSymbol: false,
          /** false：null 处断开折线，不跨空点连线 */
          connectNulls: false,
          emphasis: { focus: 'series' },
          lineStyle: {
            width: 2,
            color: LINE_COLOR
          },
          itemStyle: {
            color: LINE_COLOR,
            borderColor: '#fff',
            borderWidth: 1
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(48,131,228,0.18)' },
                { offset: 1, color: 'rgba(48,131,228,0)' }
              ]
            }
          },
          data: seriesData
        }
      ]
    }
  }, [title, data, yPercent, colHeight])

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      chartRef.current?.getEchartsInstance()?.resize()
    })
    return () => cancelAnimationFrame(frame)
  }, [chartOption, colHeight, hasData])

  return (
    <div className={className}>
      <div
        className="overview-line-chart__body"
        style={colHeight != null && colHeight > 0 ? { height: colHeight } : undefined}
      >
        <EchartReact
          ref={chartRef}
          key={`${title}-${hasData ? 1 : 0}-${Math.round(colHeight ?? 0)}`}
          style={{ width: '100%', height: '100%' }}
          option={chartOption}
          notMerge
          lazyUpdate={false}
          replaceMerge={['graphic', 'series', 'xAxis', 'yAxis', 'grid', 'title']}
        />
      </div>
    </div>
  )
}

export default styled(OverviewLineChart)`
  overflow: visible;
  border-radius: 8px;
  padding: 6px;
  border: 1px solid #ebebeb;
  background: #fff;
  width: 100%;

  .overview-line-chart__body {
    width: 100%;
  }
`
